import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PromptAndCompanyData from './PromptAndCompanyData';
import { toast } from 'react-toastify';
import KnowledgeBase from './KnowledgeBase';
import WhatsAppIntegration from './WhatsAppIntegration';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRocket, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

const generateClientBotId = () => `citasbot-${Math.random().toString(16).slice(2, 10)}`;

function AI() {
  const [shortToken, setShortToken] = useState('');
  const [step, setStep] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [promptText, setPromptText] = useState('');
  const [companyData, setCompanyData] = useState({
    name: '',
    description: '',
    contact: '',
    email: ''
  });
  const [updateKnowledgeBase, setUpdateKnowledgeBase] = useState(false);
  const [botPersonalization, setBotPersonalization] = useState({
    name: '',
    tone: ''
  });
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [isDeploying, setIsDeploying] = useState(false);
  const [deployMessage, setDeployMessage] = useState('');
  const [deployConfig, setDeployConfig] = useState({
    openrouterApiKey: '',
    model: 'moonshotai/kimi-k2-0905',
    modelSearch: '',
    botId: '',
    appUuid: '',
  });
  const [openrouterModels, setOpenrouterModels] = useState([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [showAdvancedDeploy, setShowAdvancedDeploy] = useState(false);

  useEffect(() => {
    const storedShortToken = localStorage.getItem('shortToken') || sessionStorage.getItem('shortToken');
    if (storedShortToken) {
      setShortToken(storedShortToken);
    } else {
      console.error('No se encontró el shortToken en el almacenamiento del navegador');
      toast.error('No se pudo autenticar. Por favor, inicie sesión nuevamente.');
      // Aquí podrías agregar lógica para redirigir al usuario a la página de inicio de sesión
    }
  }, []);

  useEffect(() => {
    if (shortToken) {
      // Verificar la validez del token
      axios.get('/api/verify-token', {
        headers: { Authorization: `Bearer ${shortToken}` }
      })
      .catch(error => {
        if (error.response && error.response.status === 401) {
          console.error('Token inválido o expirado');
          toast.error('Su sesión ha expirado. Por favor, inicie sesión nuevamente.');
          // Limpiar el token inválido
          localStorage.removeItem('shortToken');
          sessionStorage.removeItem('shortToken');
          setShortToken('');
          // Aquí podrías agregar lógica para redirigir al usuario a la página de inicio de sesión
        }
      });
    }
  }, [shortToken]);

  useEffect(() => {
    if (shortToken) {
      loadPromptsAndTemplates();
    }
  }, [shortToken]);

  const loadPromptsAndTemplates = async () => {
    try {
      const response = await axios.get('/api/prompts-and-templates', {
        headers: { Authorization: `Bearer ${shortToken}` }
      });
      setPrompts(response.data.prompts);
      setTemplates(response.data.templates);
      
      // Cargar la plantilla por defecto
      if (response.data.templates.length > 0) {
        const defaultTemplate = response.data.templates.find(t => t.isDefault) || response.data.templates[0];
        handleLoadTemplate(defaultTemplate);
      }
      
      toast.success('Prompts y plantillas cargados exitosamente');
    } catch (error) {
      toast.error(`Error al cargar prompts y plantillas: ${error.message}`);
    }
  };

  const handleLoadTemplate = async (template) => {
    if (template) {
      setSelectedTemplate(template);
      setPromptText(template.prompt || '');
      setCompanyData({
        name: template.companyData?.name || '',
        description: template.companyData?.description || '',
        contact: template.companyData?.contact || '',
        email: template.companyData?.email || '',
      });
      setBotPersonalization((prev) => ({
        ...prev,
        tone: template.tone || 'Neutro',
        name: template.companyData?.botName || prev.name || '',
      }));
    } else {
      setSelectedTemplate(null);
      setPromptText('');
      setCompanyData({ name: '', description: '', contact: '', email: '' });
      setBotPersonalization((prev) => ({
        ...prev,
        tone: 'Neutro',
      }));
    }
  };

  const handleSetDefaultTemplate = async (template) => {
    try {
      const response = await axios.post('/api/set-default-template', { templateName: template.name }, {
        headers: { Authorization: `Bearer ${shortToken}` }
      });
      if (response.data.success) {
        toast.success('Plantilla establecida como predeterminada');
        loadPromptsAndTemplates(); // Recargar las plantillas para reflejar el cambio
      } else {
        throw new Error(response.data.message || 'Error al establecer la plantilla predeterminada');
      }
    } catch (error) {
      console.error('Error al establecer la plantilla predeterminada:', error);
      toast.error(`Error: ${error.message}`);
    }
  };

  const DefaultTemplateButton = ({ template, onClick }) => (
    <button
      onClick={() => onClick(template)}
      className="px-3 py-1 bg-green-500 text-white rounded-full text-sm font-medium hover:bg-green-600 transition duration-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
    >
      Usar como plantilla por defecto
    </button>
  );

  const saveCurrentData = async (data) => {
    try {
      const { promptText, companyData, botPersonalization } = data;
      const templateName = botPersonalization.name || 'Plantilla sin nombre';
      
      if (!promptText.trim() && !companyData.name.trim() && !companyData.description.trim() && 
          !companyData.contact.trim() && !companyData.email.trim() && !botPersonalization.tone.trim()) {
        toast.warning('No se puede guardar una plantilla vacía. Por favor, complete al menos un campo.');
        return;
      }

      const replaceVariables = (text) => {
        return text.replace(/{{(\w+)}}/g, (_, key) => {
          switch(key) {
            case 'companyName': return companyData.name;
            case 'companyDescription': return companyData.description;
            case 'companyContact': return companyData.contact;
            case 'companyEmail': return companyData.email;
            case 'botName': return botPersonalization.name;
            case 'tone': return botPersonalization.tone;
            default: return `{{${key}}}`;
          }
        });
      };

      const interpretedPrompt = replaceVariables(promptText);

      await axios.post('/api/save-template', {
        name: templateName,
        tone: botPersonalization.tone,
        prompt: interpretedPrompt,
        companyData: companyData,
      }, {
        headers: { Authorization: `Bearer ${shortToken}` }
      });
      
      toast.success('Datos guardados correctamente');
      setDeployMessage('Cambios guardados');
      
      loadPromptsAndTemplates();
    } catch (error) {
      toast.error(`Error al guardar los datos: ${error.message}`);
      setDeployMessage('Hubo un error al guardar los cambios. Por favor, inténtelo de nuevo.');
    }
  };

  const handleDeployBot = async () => {
    setIsDeploying(true);
    setDeployMessage('');
    try {
      if (!shortToken) {
        throw new Error('Short token no disponible');
      }
      if (!deployConfig.openrouterApiKey || !deployConfig.model) {
        throw new Error('OpenRouter API key y modelo son obligatorios');
      }

      const botId = deployConfig.botId || generateClientBotId();
      if (!deployConfig.botId) {
        setDeployConfig((prev) => ({ ...prev, botId }));
      }

      const payload = {
        openrouterApiKey: deployConfig.openrouterApiKey,
        model: deployConfig.model,
        citasproToken: shortToken,
        botId,
        appUuid: deployConfig.appUuid || undefined,
      };

      const response = await axios.post('/api/ai/deploy', payload, {
        headers: { Authorization: `Bearer ${shortToken}` },
      });

      const data = response?.data?.data || {};
      setDeployConfig((prev) => ({
        ...prev,
        appUuid: data.appUuid || prev.appUuid,
        botId: data.botId || prev.botId,
      }));

      const action = data.mode === 'updated' ? 'actualizado' : 'desplegado';
      setDeployMessage(`Bot ${action}. appUuid: ${data.appUuid || 'n/a'} | botId: ${data.botId || 'n/a'}`);
      toast.success(`Bot ${action} correctamente`);
    } catch (error) {
      console.error('Error al desplegar el bot:', error);
      const details =
        error?.response?.data?.details ||
        error?.response?.data?.error ||
        error?.message ||
        'Error desconocido';
      setDeployMessage(`Error al desplegar: ${details}`);
      toast.error(`Error al desplegar: ${details}`);
    } finally {
      setIsDeploying(false);
    }
  };

  const handleLoadOpenRouterModels = async () => {
    try {
      if (!shortToken) {
        throw new Error('Short token no disponible');
      }
      if (!deployConfig.openrouterApiKey) {
        throw new Error('Introduce tu OpenRouter API key');
      }
      setIsLoadingModels(true);
      const response = await axios.post('/api/ai/openrouter/models', {
        openrouterApiKey: deployConfig.openrouterApiKey,
      }, {
        headers: { Authorization: `Bearer ${shortToken}` },
      });
      const models = Array.isArray(response?.data?.data) ? response.data.data : [];
      setOpenrouterModels(models);
      if (!deployConfig.model && models[0]?.id) {
        setDeployConfig((prev) => ({ ...prev, model: models[0].id }));
      }
      toast.success(`Modelos cargados: ${models.length}`);
    } catch (error) {
      const details =
        error?.response?.data?.details ||
        error?.response?.data?.error ||
        error?.message ||
        'No se pudo cargar modelos';
      toast.error(details);
    } finally {
      setIsLoadingModels(false);
    }
  };


  const handleSaveTemplate = async (newTemplate) => {
    try {
      if (!newTemplate.name || !promptText.trim()) {
        toast.error('El nombre de la plantilla y el texto del prompt son obligatorios');
        return;
      }

      const templateToSave = {
        name: newTemplate.name,
        prompt: promptText.trim(),
        companyData: {
          name: companyData.name || '',
          description: companyData.description || '',
          contact: companyData.contact || '',
          email: companyData.email || ''
        },
        botPersonalization: {
          name: botPersonalization.name || '',
          tone: botPersonalization.tone || ''
        },
        tone: botPersonalization.tone || ''
      };

      const response = await axios.post('/api/save-template', templateToSave, {
        headers: { Authorization: `Bearer ${shortToken}` }
      });

      if (response.data.success) {
        const updatedTemplates = [...templates, templateToSave];
        setTemplates(updatedTemplates);
        toast.success('Plantilla guardada correctamente');
      } else {
        throw new Error(response.data.message || 'Error desconocido al guardar la plantilla');
      }
    } catch (error) {
      console.error('Error al guardar la plantilla:', error);
      if (error.response && error.response.status === 401) {
        toast.error('Error de autenticación. Por favor, inicie sesión nuevamente.');
        // Aquí podrías agregar lógica para redirigir al usuario a la página de inicio de sesión
      } else if (error.response && error.response.data && error.response.data.error === 'Token inválido') {
        toast.error('Su sesión ha expirado. Por favor, inicie sesión nuevamente.');
        // Aquí podrías agregar lógica para redirigir al usuario a la página de inicio de sesión
      } else {
        toast.error(`Error al guardar la plantilla: ${error.response?.data?.message || error.message}`);
      }
    }
  };

  const handleDeleteTemplate = async (templateName) => {
    try {
      await axios.delete(`/api/delete-template/${encodeURIComponent(templateName)}`, {
        headers: { Authorization: `Bearer ${shortToken}` }
      });
      const updatedTemplates = templates.filter(t => t.name !== templateName);
      setTemplates(updatedTemplates);
      setSelectedTemplate(null);
      toast.success('Plantilla eliminada correctamente');
    } catch (error) {
      console.error('Error al eliminar la plantilla:', error);
      toast.error('Error al eliminar la plantilla');
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePromptChange = (newPrompt) => {
    setPromptText(newPrompt);
  };

  const handleCompanyDataChange = (newData) => {
    setCompanyData(newData);
  };

  const handleNextStep = () => {
    setStep(prevStep => prevStep + 1);
    if (step === 1) {
      setUpdateKnowledgeBase(prev => !prev);
    }
  };

  const handlePrevStep = () => {
    setStep(prevStep => prevStep - 1);
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return <PromptAndCompanyData 
          promptText={promptText}
          companyData={companyData}
          botPersonalization={botPersonalization}
          onPromptChange={handlePromptChange}
          onCompanyDataChange={handleCompanyDataChange}
          onBotPersonalizationChange={handleBotPersonalizationChange}
          templates={templates}
          onLoadTemplate={handleLoadTemplate}
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
          saveCurrentData={saveCurrentData}
          onSaveTemplate={handleSaveTemplate}
          onDeleteTemplate={handleDeleteTemplate}
          onSetDefaultTemplate={handleSetDefaultTemplate}
          DefaultTemplateButton={DefaultTemplateButton}
        />;
      case 2:
        return <KnowledgeBase key={updateKnowledgeBase} />;
      case 3:
        {
          const filteredModels = openrouterModels.filter((item) => {
            const query = (deployConfig.modelSearch || '').toLowerCase().trim();
            if (!query) return true;
            return String(item.id || '').toLowerCase().includes(query) ||
              String(item.name || '').toLowerCase().includes(query);
          });
        return (
          <>
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl p-4 md:p-6 mb-4 shadow-lg">
              <h3 className="text-lg md:text-xl font-semibold mb-2">Despliegue automático del bot</h3>
              <p className="text-sm md:text-base opacity-95 mb-4">
                Solo necesitas tu OpenRouter API key y elegir un modelo. El token de CitasPro se toma automáticamente de tu sesión.
              </p>
              <div className="flex flex-col md:flex-row gap-4 items-start">
                <div className="bg-white p-3 rounded-lg">
                  <img
                    src="https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=https%3A%2F%2Fopenrouter.ai%2Fkeys"
                    alt="QR OpenRouter Keys"
                    className="w-[140px] h-[140px]"
                  />
                </div>
                <div className="text-sm">
                  <p className="font-semibold">Cómo obtener tu key de OpenRouter</p>
                  <p>1. Abre https://openrouter.ai/keys (o escanea el QR).</p>
                  <p>2. Crea una API key nueva.</p>
                  <p>3. Pégala abajo y pulsa "Cargar modelos".</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6 mb-4 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Configuración mínima</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">OpenRouter API key</label>
                  <input
                    type="password"
                    value={deployConfig.openrouterApiKey}
                    onChange={(e) => setDeployConfig((prev) => ({ ...prev, openrouterApiKey: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="sk-or-v1-..."
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Buscar modelo</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={deployConfig.modelSearch}
                      onChange={(e) => setDeployConfig((prev) => ({ ...prev, modelSearch: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      placeholder="gpt, claude, kimi..."
                    />
                    <button
                      type="button"
                      onClick={handleLoadOpenRouterModels}
                      disabled={isLoadingModels}
                      className={`px-3 py-2 rounded-lg text-white ${isLoadingModels ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                      {isLoadingModels ? '...' : 'Cargar modelos'}
                    </button>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-700 mb-1">Modelo seleccionado</label>
                  <select
                    value={deployConfig.model}
                    onChange={(e) => setDeployConfig((prev) => ({ ...prev, model: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    {filteredModels.length === 0 && <option value={deployConfig.model}>{deployConfig.model || 'Sin modelos cargados'}</option>}
                    {filteredModels.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.id}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-700 mb-1">Modelo manual (opcional)</label>
                  <input
                    type="text"
                    value={deployConfig.model}
                    onChange={(e) => setDeployConfig((prev) => ({ ...prev, model: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="moonshotai/kimi-k2-0905"
                  />
                </div>
                <div className="md:col-span-2">
                  <button
                    type="button"
                    onClick={() => setShowAdvancedDeploy((prev) => !prev)}
                    className="text-sm text-blue-700 hover:text-blue-900 underline"
                  >
                    {showAdvancedDeploy ? 'Ocultar opciones avanzadas' : 'Mostrar opciones avanzadas'}
                  </button>
                </div>
                {showAdvancedDeploy && (
                  <>
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Bot ID (opcional)</label>
                      <input
                        type="text"
                        value={deployConfig.botId}
                        onChange={(e) => setDeployConfig((prev) => ({ ...prev, botId: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        placeholder="citasbot-50b4b47d"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">App UUID (opcional)</label>
                      <input
                        type="text"
                        value={deployConfig.appUuid}
                        onChange={(e) => setDeployConfig((prev) => ({ ...prev, appUuid: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        placeholder="hwkkgkwsssgg4go8k00cskkw"
                      />
                    </div>
                  </>
                )}
                <div className="md:col-span-2 text-xs text-gray-500">
                  No te pedimos token CitasPro: se usa automáticamente el de tu sesión autenticada.
                </div>
              </div>
            </div>
            {deployMessage && (
              <div className="mt-4 p-4 bg-blue-100 text-blue-700 rounded">
                {deployMessage}
              </div>
            )}
            <WhatsAppIntegration />
          </>
        );
        }
      default:
        return null;
    }
  };

  const handleBotPersonalizationChange = (newData) => {
    setBotPersonalization(newData);
  };

  return (
    <div className="w-full p-4 md:p-8 bg-gray-100 min-h-screen">
      <h1 className="text-3xl md:text-4xl font-bold mb-6 md:mb-8 text-center text-blue-600">Configuración del Agente IA</h1>
      
      <div className="mb-6 flex justify-center">
        <div className="flex items-center">
          {[1, 2, 3].map((stepNumber) => (
            <React.Fragment key={stepNumber}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === stepNumber ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-600'}`}>
                {stepNumber}
              </div>
              {stepNumber < 3 && <div className="w-16 h-1 bg-gray-300 mx-2"></div>}
            </React.Fragment>
          ))}
        </div>
      </div>

      {renderStep()}

      <div className="mt-8 flex justify-center items-center space-x-4">
        {step > 1 && (
          <button
            onClick={handlePrevStep}
            className="px-6 py-2 bg-gray-500 text-white rounded-full hover:bg-gray-600 transition duration-300 shadow-md flex items-center space-x-2"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="mr-2" />
            <span>Anterior</span>
          </button>
        )}
        {step === 3 && (
          <button
            onClick={handleDeployBot}
            disabled={isDeploying}
            className={`px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full hover:from-blue-600 hover:to-purple-700 transition-all duration-300 ${isDeploying ? 'opacity-50 cursor-not-allowed' : ''} flex items-center space-x-2 group relative overflow-hidden shadow-md`}
          >
            <span className="relative z-10">{isDeploying ? 'Desplegando...' : 'Desplegar Bot'}</span>
            <FontAwesomeIcon 
              icon={faRocket} 
              className={`relative z-10 transform ${isDeploying ? '' : 'group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:rotate-12'} transition-all duration-300`}
            />
            <div className="absolute top-0 left-0 w-full h-full bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
          </button>
        )}
        {step < 3 && (
          <button
            onClick={handleNextStep}
            className="px-6 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition duration-300 shadow-md flex items-center space-x-2"
          >
            <span>Siguiente</span>
            <FontAwesomeIcon icon={faChevronRight} className="ml-2" />
          </button>
        )}
      </div>
    </div>
  );
}

export default AI;
